import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { X, Sparkles } from 'lucide-react';

/**
 * Standardized layout wrapper for all dashboard pages
 * Ensures consistent spacing, typography, structure, and guide positioning
 */
const DashboardPageLayout = ({ 
  title, 
  subtitle, 
  actions,
  guideIcon,
  guideTitle,
  guideDescription,
  guideActions,
  onGuideActionClick,
  guideChildren,
  children 
}) => {
  const [showGuide, setShowGuide] = useState(() => {
    return localStorage.getItem('hideStepGuide') !== 'true';
  });

  // Listen for guide visibility changes
  useEffect(() => {
    const handleGuideChange = () => {
      setShowGuide(localStorage.getItem('hideStepGuide') !== 'true');
    };
    window.addEventListener('guideVisibilityChanged', handleGuideChange);
    return () => window.removeEventListener('guideVisibilityChanged', handleGuideChange);
  }, []);

  const handleDismiss = () => {
    setShowGuide(false);
    localStorage.setItem('hideStepGuide', 'true');
    window.dispatchEvent(new Event('guideVisibilityChanged'));
  };

  return (
    <div className="space-y-6">
      {/* Guide - ALWAYS FIRST, ABOVE TITLE */}
      {showGuide && (guideIcon || guideTitle || guideDescription || guideChildren) && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            {guideChildren ? (
              guideChildren
            ) : (
              <div className="flex items-start md:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-start md:items-center gap-3">
                    {guideIcon && (
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
                        {guideIcon}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {guideTitle && <h3 className="font-semibold text-gray-900">{guideTitle}</h3>}
                      {guideDescription && (
                        <p className="text-sm text-gray-600 mt-1">{guideDescription}</p>
                      )}
                      
                      {/* Guide action buttons */}
                      {guideActions && guideActions.length > 0 && (
                        <div className="flex gap-2 mt-3">
                          {guideActions.map((action, index) => (
                            <Button
                              key={index}
                              size="sm"
                              variant="outline"
                              onClick={() => onGuideActionClick && onGuideActionClick(action.action)}
                              className="bg-white hover:bg-blue-100 border-blue-300 text-blue-700 font-medium"
                            >
                              {action.icon === '✨' && <Sparkles className="w-4 h-4 mr-1" />}
                              {action.label}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  className="text-gray-500 hover:text-gray-700 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Dismiss</span>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Page Header - AFTER GUIDE */}
      {(title || subtitle || actions) && (
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1 space-y-1">
            {title && (
              <h1 className="text-3xl font-bold text-gray-900">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="text-base text-gray-600">
                {subtitle}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 flex-shrink-0">
              {actions}
            </div>
          )}
        </div>
      )}
      
      {/* Page Content */}
      <div>
        {children}
      </div>
    </div>
  );
};

export default DashboardPageLayout;
